import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import testIds from "constants/testIds";
import { getAdressTypeOption } from "api";
import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import SearchSelect from "../../Common/SearchSelect";
import { IAddressOption, IPortLinkedAddress } from "../Types";

interface PortFormProps {
  partyType: "notify_party" | "consignee";
  portAddressOption: any[];
  getTimeZones: Function;
  setSearch: Dispatch<SetStateAction<string>>;
  disabled: boolean;
  required: boolean;
}

export default function PortForm({
  partyType,
  portAddressOption,
  getTimeZones,
  setSearch,
  disabled,
  required,
}: PortFormProps) {
  const intl = useIntl();
  const { permissions }: any = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions)?.company_shipper_settings;
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const { getValues, setValue, control } = useFormContext();
  const clearSelectedAddress = useCallback(() => {
    setValue(`schedule.${partyType}.address_reference_number`, "");
    setValue(`schedule.${partyType}.address_name`, "");
    setValue(`schedule.${partyType}.address_street`, "");
    setValue(`schedule.${partyType}.address_street_line2`, "");
    setValue(`schedule.${partyType}.address_city`, "");
    setValue(`schedule.${partyType}.address_state`, "");
    setValue(`schedule.${partyType}.address_country`, "");
    setValue(`schedule.${partyType}.address_zipcode`, "");
    setValue(`schedule.${partyType}.address_un_locode`, "");
  }, [partyType, setValue]);
  const clearSelectedPort = useCallback(() => {
    setValue(`schedule.${partyType}.seaport`, "");
    setValue(`schedule.${partyType}.airport`, "");
    setValue(`schedule.${partyType}.seaport_name`, "");
    setValue(`schedule.${partyType}.airport_name`, "");
    setValue(`schedule.${partyType}.port_city`, "");
    setValue(`schedule.${partyType}.port_country`, "");
    setValue(`schedule.${partyType}.port_country_code`, "");
    setValue(`schedule.${partyType}.timezone`, "");
  }, [partyType, setValue]);

  const handleFillPortAddressOption = useCallback(
    (event, options: { preserveSelectedAddress?: boolean } = {}) => {
      const address = event[0];
      if (!address) return;
      if (!options.preserveSelectedAddress) {
        clearSelectedAddress();
      }
      const linkedAddressTimezone = (address?.address as IPortLinkedAddress | undefined)?.timezone || "";
      const preferredTimezone = linkedAddressTimezone || getValues(`schedule.${partyType}.timezone`);

      if (getValues(`schedule.${partyType}.address_type`) === "seaport") {
        getTimeZones(true, address?.country_code, isOptionalPortAddressFormEnabled ? preferredTimezone : "");
        setValue(`schedule.${partyType}.seaport_name`, address?.city_name);
        setValue(`schedule.${partyType}.seaport`, address?.code);
      } else {
        getTimeZones(true, address?.country_code, isOptionalPortAddressFormEnabled ? preferredTimezone : "");
        setValue(`schedule.${partyType}.airport_name`, address?.city_name);
        setValue(`schedule.${partyType}.airport`, address?.code);
      }

      if (address.city_name) {
        setValue(`schedule.${partyType}.port_city`, address.city_name, { shouldValidate: true });
      }
      if (address.country_name) {
        setValue(`schedule.${partyType}.port_country`, address.country_name, { shouldValidate: true });
      }
      if (address.country_code) {
        setValue(`schedule.${partyType}.port_country_code`, address.country_code, { shouldValidate: true });
      }
    },
    [clearSelectedAddress, getTimeZones, getValues, isOptionalPortAddressFormEnabled, partyType, setValue],
  );

  // On initial mount of an edited / duplicated draft, the airport/seaport
  // code+name fields are prefilled but the SearchSelect treats them as a
  // free-string. Re-resolve the prefilled code (or name) against the API and
  // simulate a selection to populate the linked address.
  //
  // NOTE: the form is hydrated asynchronously via methods.reset(...) AFTER
  // this component mounts. We MUST react to the prefilled fields appearing,
  // so we read them via useWatch instead of getValues-on-mount.
  const watchedPartyAddressType = useWatch({ control, name: `schedule.${partyType}.address_type` });
  const watchedPartyAirportCode = useWatch({ control, name: `schedule.${partyType}.airport` });
  const watchedPartyAirportName = useWatch({ control, name: `schedule.${partyType}.airport_name` });
  const watchedPartySeaportCode = useWatch({ control, name: `schedule.${partyType}.seaport` });
  const watchedPartySeaportName = useWatch({ control, name: `schedule.${partyType}.seaport_name` });
  const lastHydratedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOptionalPortAddressFormEnabled) return;
    if (watchedPartyAddressType !== "airport" && watchedPartyAddressType !== "seaport") return;
    const code = watchedPartyAddressType === "airport" ? watchedPartyAirportCode : watchedPartySeaportCode;
    const portName = watchedPartyAddressType === "airport" ? watchedPartyAirportName : watchedPartySeaportName;
    const query = code || portName;
    if (!query) return;
    if (lastHydratedQueryRef.current === query) return;
    lastHydratedQueryRef.current = query;
    const endpoint = watchedPartyAddressType === "airport" ? "/common/airport" : "/common/seaport";
    getAdressTypeOption(endpoint, query)
      .then((res) => {
        const first = res?.data?.results?.[0];
        if (!first) return;
        const hasDraftSelectedAddress = Boolean(getValues(`schedule.${partyType}.address_reference_number`));
        handleFillPortAddressOption([
          {
            id: first.id,
            label: first.name,
            value: first.name,
            city_name: first.city_name,
            country_name: first.country_name,
            country_code: first.country_code,
            code: first.un_locode || first.code,
            address: first.address,
          },
        ], { preserveSelectedAddress: hasDraftSelectedAddress });
      })
      .catch(() => {
        // best-effort hydration; allow retry on next value change
        lastHydratedQueryRef.current = null;
      });
  }, [
    isOptionalPortAddressFormEnabled,
    partyType,
    watchedPartyAddressType,
    watchedPartyAirportCode,
    watchedPartyAirportName,
    watchedPartySeaportCode,
    watchedPartySeaportName,
    handleFillPortAddressOption,
  ]);

  const isRequired = () => {
    const addressType = getValues(`schedule.${partyType}.address_type`);
    if (required) {
      return addressType === "airport" ? "Please select a valid airport" : "Please select a valid port";
    }
    return false;
  };

  return (
    <div className="w-192">
      <SearchSelect
        preselect={false}
        testId={
          partyType === "notify_party"
            ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_PORT_SELECT
            : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_PORT_SELECT
        }
        name={
          getValues(`schedule.${partyType}.address_type`) !== "address" &&
          getValues(`schedule.${partyType}.address_type`) === "airport"
            ? `schedule.${partyType}.airport_name`
            : `schedule.${partyType}.seaport_name`
        }
        required={isRequired()}
        options={portAddressOption.map((option: IAddressOption) => ({
          id: option.id,
          label: option.name,
          value: option.name,
          city_name: option.city_name,
          country_name: option.country_name,
          country_code: option.country_code,
          code: option.un_locode || option.code,
          address: option.address,
        }))}
        label={
          !enable_new_address_structure
            ? ""
            : getValues(`schedule.${partyType}.address_type`) === "airport"
            ? intl.formatMessage({
                id: "new-request.schedule.airport",
                defaultMessage: "Search airport",
              })
            : intl.formatMessage({
                id: "new-request.schedule.port",
                defaultMessage: "Search port",
              })
        }
        placeholder={
          getValues(`schedule.${partyType}.address_type`) === "airport"
            ? intl.formatMessage({
                id: "new-request.schedule.autoCompletePlaceholder.searchAirport",
                defaultMessage: "Search airport",
              })
            : intl.formatMessage({
                id: "new-request.schedule.autoCompletePlaceholder.searchPort",
                defaultMessage: "Search port",
              })
        }
        filterBy={["label", "city_name", "country_name", "code"]}
        handleRenderOption={handleRenderAddressOption}
        onInputChange={(val: string) => {
          setSearch(val);
          if (!val?.trim()) {
            clearSelectedPort();
            clearSelectedAddress();
          }
        }}
        onSelect={handleFillPortAddressOption}
        disable={disabled}
      />
    </div>
  );
}
